import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, Area, AreaChart
} from 'recharts';
import {
  TrendingUp, Users, MousePointerClick, Clock, Eye,
  ArrowUpRight, ArrowDownRight, BarChart3, PieChart as PieIcon, Activity
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = ['#002FA7', '#34C759', '#FF9500', '#FF3B30', '#AF52DE', '#00C7BE', '#FF2D55', '#5856D6'];

const AnalyticsDashboardPage = () => {
  const { token, user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [timeline, setTimeline] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const opts = { headers };
      const [summaryRes, timelineRes] = await Promise.all([
        axios.get(`${API}/analytics/demo/summary`, opts),
        axios.get(`${API}/analytics/demo/timeline?days=30`, opts)
      ]);
      setSummary(summaryRes.data);
      setTimeline(timelineRes.data);
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error('Admin access required to view analytics');
      } else {
        toast.error('Failed to load analytics data');
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div data-testid="analytics-dashboard-page">
        <h1 className="text-2xl font-semibold text-[#0A0A0A] mb-6">Demo Analytics</h1>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-[#002FA7] border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  if (!summary) {
    return (
      <div data-testid="analytics-dashboard-page">
        <h1 className="text-2xl font-semibold text-[#0A0A0A] mb-6">Demo Analytics</h1>
        <Card className="border-[#E5E7EB]">
          <CardContent className="p-12 text-center text-[#9CA3AF]">
            <BarChart3 className="w-12 h-12 mx-auto mb-4 text-[#E5E7EB]" />
            <p>No analytics data available yet, or you don't have admin access.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const eventCountsData = Object.entries(summary.event_counts || {}).map(([name, value]) => ({
    name: name.replace(/_/g, ' '),
    value
  }));

  const tabViewsData = Object.entries(summary.tab_views || {}).map(([name, value]) => ({
    name,
    views: value
  }));

  const ctaData = Object.entries(summary.cta_clicks || {}).map(([name, value]) => ({
    name,
    clicks: value
  }));

  const tooltipData = Object.entries(summary.tooltip_views || {}).map(([name, value]) => ({
    name: name.replace(/_/g, ' '),
    views: value
  })).slice(0, 10);

  const totalEvents = Object.values(summary.event_counts || {}).reduce((a, b) => a + b, 0);
  const totalCTA = Object.values(summary.cta_clicks || {}).reduce((a, b) => a + b, 0);

  return (
    <div data-testid="analytics-dashboard-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-[#0A0A0A]">Demo Analytics</h1>
          <p className="text-[#4B5563] text-sm">Track demo page engagement and conversion metrics</p>
        </div>
        <Button variant="outline" onClick={fetchData} data-testid="refresh-analytics-btn">
          <Activity className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Card className="border-[#E5E7EB]">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#4B5563]">Total Sessions</p>
                <p className="text-2xl font-bold text-[#0A0A0A]">{summary.total_sessions}</p>
              </div>
              <div className="w-10 h-10 bg-[#002FA7] flex items-center justify-center rounded">
                <Users className="w-5 h-5 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-[#E5E7EB]">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#4B5563]">Total Events</p>
                <p className="text-2xl font-bold text-[#0A0A0A]">{totalEvents}</p>
              </div>
              <div className="w-10 h-10 bg-[#34C759] flex items-center justify-center rounded">
                <Eye className="w-5 h-5 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-[#E5E7EB]">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#4B5563]">CTA Clicks</p>
                <p className="text-2xl font-bold text-[#0A0A0A]">{totalCTA}</p>
              </div>
              <div className="w-10 h-10 bg-[#FF9500] flex items-center justify-center rounded">
                <MousePointerClick className="w-5 h-5 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-[#E5E7EB]">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#4B5563]">Avg Session</p>
                <p className="text-2xl font-bold text-[#0A0A0A]">{summary.avg_session_duration_seconds}s</p>
              </div>
              <div className="w-10 h-10 bg-[#AF52DE] flex items-center justify-center rounded">
                <Clock className="w-5 h-5 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="bg-white border border-[#E5E7EB]">
          <TabsTrigger value="overview" data-testid="analytics-tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="engagement" data-testid="analytics-tab-engagement">Engagement</TabsTrigger>
          <TabsTrigger value="conversion" data-testid="analytics-tab-conversion">Conversion</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Sessions Over Time */}
            {timeline?.sessions_per_day?.length > 0 && (
              <Card className="border-[#E5E7EB]">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base font-semibold">Sessions Over Time</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={250}>
                    <AreaChart data={timeline.sessions_per_day}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                      <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(d) => d.slice(5)} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip />
                      <Area type="monotone" dataKey="sessions" stroke="#002FA7" fill="#002FA7" fillOpacity={0.15} />
                    </AreaChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}

            {/* Event Type Distribution */}
            <Card className="border-[#E5E7EB]">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-semibold">Event Distribution</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie data={eventCountsData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                      {eventCountsData.map((_, idx) => (
                        <Cell key={idx} fill={COLORS[idx % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Engagement Tab */}
        <TabsContent value="engagement" className="space-y-6">
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Tab Views */}
            <Card className="border-[#E5E7EB]">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-semibold">Most Viewed Tabs</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={tabViewsData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Bar dataKey="views" fill="#002FA7" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Tooltip Engagement */}
            <Card className="border-[#E5E7EB]">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-semibold">Tooltip Engagement</CardTitle>
              </CardHeader>
              <CardContent>
                {tooltipData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={tooltipData} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                      <XAxis type="number" tick={{ fontSize: 11 }} />
                      <YAxis dataKey="name" type="category" tick={{ fontSize: 10 }} width={100} />
                      <Tooltip />
                      <Bar dataKey="views" fill="#34C759" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[250px] flex items-center justify-center text-[#9CA3AF] text-sm">
                    No tooltip interaction data yet
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Conversion Tab */}
        <TabsContent value="conversion" className="space-y-6">
          <div className="grid lg:grid-cols-2 gap-6">
            {/* CTA Click Distribution */}
            <Card className="border-[#E5E7EB]">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-semibold">CTA Click Types</CardTitle>
              </CardHeader>
              <CardContent>
                {ctaData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={ctaData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                      <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip />
                      <Bar dataKey="clicks" fill="#FF9500" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[250px] flex items-center justify-center text-[#9CA3AF] text-sm">
                    No CTA click data yet
                  </div>
                )}
              </CardContent>
            </Card>

            {/* CTA Clicks Over Time */}
            {timeline?.cta_per_day?.length > 0 && (
              <Card className="border-[#E5E7EB]">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base font-semibold">CTA Clicks Over Time</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={250}>
                    <LineChart data={timeline.cta_per_day}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                      <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(d) => d.slice(5)} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip />
                      <Line type="monotone" dataKey="clicks" stroke="#FF9500" strokeWidth={2} dot={{ r: 4 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}

            {/* Conversion Funnel Summary */}
            <Card className="border-[#E5E7EB] lg:col-span-2">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-semibold">Conversion Funnel</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-center gap-4 py-6">
                  {[
                    { label: 'Page Views', value: summary.event_counts?.page_view || 0, color: '#002FA7' },
                    { label: 'Tab Engaged', value: Object.values(summary.tab_views || {}).reduce((a, b) => a + b, 0), color: '#34C759' },
                    { label: 'Feature Clicks', value: summary.event_counts?.feature_engagement || 0, color: '#AF52DE' },
                    { label: 'CTA Clicks', value: totalCTA, color: '#FF9500' },
                    { label: 'Signups', value: summary.cta_clicks?.signup || 0, color: '#FF3B30' }
                  ].map((step, i, arr) => (
                    <div key={step.label} className="flex items-center gap-4">
                      <div className="text-center">
                        <div className="w-16 h-16 rounded-full flex items-center justify-center text-white font-bold text-lg mx-auto mb-2" style={{ backgroundColor: step.color }}>
                          {step.value}
                        </div>
                        <p className="text-xs text-[#4B5563] font-medium">{step.label}</p>
                      </div>
                      {i < arr.length - 1 && (
                        <ArrowUpRight className="w-5 h-5 text-[#9CA3AF] flex-shrink-0" />
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AnalyticsDashboardPage;
